import { App } from 'vue';
import { ElMessageBox } from 'element-plus';

interface VersionCheckerOptions {
  interval?: number;
  versionKey?: string;
}

interface VersionInfo {
  version?: string;
}

let isShowingUpdateModal = false;

const defaultOptions: Required<VersionCheckerOptions> = {
  interval: 60000,
  versionKey: 'STUDY_ABROAD_PROJECT_VERSION',
};

const fetchVersion = async (): Promise<VersionInfo> => {
  const baseUrl = process.env.BASE_URL || '/';
  const url = `${window.location.origin}${baseUrl}version-info.json?timestamp=${Date.now()}`;
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error(`版本文件请求失败：${response.status}`);
  return response.json();
};

const getPendingVersionKey = (versionKey: string): string => `${versionKey}:pending`;

const completePendingVersion = (versionKey: string): void => {
  const pendingVersionKey = getPendingVersionKey(versionKey);
  const pendingVersion = sessionStorage.getItem(pendingVersionKey);
  if (!pendingVersion) return;

  localStorage.setItem(versionKey, pendingVersion);
  sessionStorage.removeItem(pendingVersionKey);
};

const showUpdatePrompt = (projectVersion: string, versionKey: string): void => {
  if (isShowingUpdateModal) return;
  isShowingUpdateModal = true;

  ElMessageBox.confirm(
    '当前系统有更新，如果您此时在页面中没有进行填写内容等操作，请点击【立即刷新】使用最新版本！',
    '版本更新提示',
    {
      confirmButtonText: '立即刷新',
      cancelButtonText: '稍后刷新',
      closeOnClickModal: false,
      showClose: false,
      type: 'warning',
    },
  )
    .then(() => {
      sessionStorage.setItem(getPendingVersionKey(versionKey), projectVersion);
      window.location.reload();
    })
    .catch(() => undefined);
};

const createVersionChecker = (options: VersionCheckerOptions = {}) => {
  const { interval, versionKey } = { ...defaultOptions, ...options };

  const checkVersion = async (): Promise<void> => {
    try {
      completePendingVersion(versionKey);

      const projectVersion = (await fetchVersion()).version;
      if (!projectVersion) return;

      const localVersion = localStorage.getItem(versionKey);
      if (!localVersion) {
        localStorage.setItem(versionKey, projectVersion);
        return;
      }

      if (localVersion !== projectVersion) {
        showUpdatePrompt(projectVersion, versionKey);
      }
    } catch (error) {
      console.error('版本检测失败：', error);
    }
  };

  checkVersion();
  window.setInterval(checkVersion, interval);
};

export default {
  install(_app: App, options?: VersionCheckerOptions): void {
    if (process.env.ENV === 'development' || process.env.ENV === 'production') {
      createVersionChecker(options);
    }
  },
};
